import {Directive, effect, ElementRef, Inject, InjectionToken, input, OnDestroy, OnInit, Renderer2, signal} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {Arbeidskø} from "felles-ng2/tjenester/Arbeidskø";
import {ElementSynlighetObservering} from "felles-ng2/tjenester/ElementSynlighetObservering";
import {IMAGE_LOADER, ImageLoader} from "@angular/common";

const debug = initDebugger('LazyBildelastingDirektiv');

const transparentBilde = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVQYV2NgYAAAAAMAAWgmWQ0AAAAASUVORK5CYII=';

/**  InjectionToken for injisering av {@link Arbeidskø} instans i {@link LazyBildelastingDirektiv}. */
export const LazyBildelastingDirektiv_ArbeidskøTjeneste_DI_TOKEN = new InjectionToken<Arbeidskø<undefined>>('ArbeidskøTjeneste for LazyBildelastingDirektiv');

/**  InjectionToken for injisering av {@link ElementSynlighetObservering} instans i {@link LazyBildelastingDirektiv}. */
export const LazyBildelastingDirektiv_ElementSynlighetObservering_DI_TOKEN = new InjectionToken<ElementSynlighetObservering>('ElementSynlighetObservering for LazyBildelastingDirektiv');


/**
 * Venter med å sette src på img element til elementet er i viewport.
 * Sørger også for at det kun skjer et begrenset antall samtidige bildelastinger, antallet er satt i
 * injisert {@link Arbeidskø}.
 *
 * Benytter DOM apiet IntersectionObserver:
 * The IntersectionObserver interface of the Intersection Observer API provides a way to asynchronously observe changes
 * in the intersection of a target element with an ancestor element or with a top-level document's viewport.
 * The ancestor element or viewport is referred to as the root.
 * Se https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver
 *
 * Bruk:
 * ```
 * <img [lazyBildelasting]="bildeUrl">
 * ```
 */
@Directive({
    selector: 'img[lazyBildelasting]',
    standalone: true,
    host: {'[class.ikke-lastet]': 'ikkeLastet()'}
})
export class LazyBildelastingDirektiv implements OnInit, OnDestroy {

    bildeUrl = input.required<string>({alias: 'lazyBildelasting'});

    ikkeLastet = signal(true);

    constructor(private el: ElementRef<HTMLImageElement>,
                private renderer: Renderer2,
                @Inject(LazyBildelastingDirektiv_ElementSynlighetObservering_DI_TOKEN) private elementSynlighet: ElementSynlighetObservering,
                @Inject(LazyBildelastingDirektiv_ArbeidskøTjeneste_DI_TOKEN) private arbeidskøTjeneste: Arbeidskø<undefined>,
                @Inject(IMAGE_LOADER) private imageLoader: ImageLoader) {

        //sørg for at endringer i bildeUrl får effekt
        effect(() => {
            if (!this.ikkeLastet()) this.settImgSrc(this.bildeUrl());
        });
    }

    ngOnInit(): void {
        if (erTomEllerNil(this.bildeUrl())) {
            console.error('lazyBildelasting må være satt til en ikke-tom URL-streng');
            return;
        }

        this.settImgSrc(transparentBilde);
        this.initierLastingAvBildeNårElementVises();
    }

    ngOnDestroy(): void {
        this.elementSynlighet.avregistrer(this.el.nativeElement);
    }

    //private hjelpemetoder

    private initierLastingAvBildeNårElementVises() {
        this.elementSynlighet.nårElementBlirSynlig(this.el.nativeElement, () => this.lastBilde())
    }

    private lastBilde(): void {
        //registrer arbeid som setter src på img
        const arbeid = this.arbeidskøTjeneste.register(arbeidId => {
            debug(`setter img.src=${this.bildeUrl()} (arbeid:${arbeidId})`);
            this.ikkeLastet.set(false);
            this.settImgSrc(this.bildeUrl());
        });
        //marker arbeidet som avsluttet når load eventet er mottatt for dette img
        this.renderer.listen(this.el.nativeElement, 'load', () => arbeid.avslutt(undefined));
        //eller dersom lasting av bildet feiler
        this.renderer.listen(this.el.nativeElement, 'error', () => arbeid.avslutt(undefined));

        debug(`initialiserer lasting av bilde ${this.bildeUrl()} (arbeid:${arbeid.id})`);
    }

    private settImgSrc(url: string) {
        //går via imageLoader primært for at imageLoader konf ved kjøring av tester skal ha effekt
        const endeligUrl = this.imageLoader({src:url})
        this.renderer.setAttribute(this.el.nativeElement, 'src', endeligUrl);
    }

}
